import { useEffect } from 'react'
import { useMap } from 'react-leaflet'
import L from 'leaflet'
import { getPinAnimations, getPinCascadeDuration } from './filterAnimation'

const OPENING_CLASS = 'map-opening'

interface PinCascadeProps {
  isRevealed: boolean
}

/**
 * Headless component that pops in the pins and cluster badges on the map as it is revealed, rippling
 * out from its centre. Each icon gets its animations and its scale's origin through variables, and the
 * animations only run while the map container has the opening class (map.css): markercluster re-creates
 * the icons on every pan and zoom, so the class is taken off once the cascade is over, or as soon as the
 * map moves.
 */
const PinCascade = ({ isRevealed }: PinCascadeProps) => {
  const map = useMap()

  useEffect(() => {
    if (!isRevealed) return

    const container = map.getContainer()
    // the map may still be scaled here, but only the ratio of the distances matters
    const bounds = container.getBoundingClientRect()
    const centerX = bounds.left + bounds.width / 2
    const centerY = bounds.top + bounds.height / 2
    const halfDiagonal = Math.hypot(bounds.width, bounds.height) / 2

    container.querySelectorAll<HTMLElement>('.leaflet-marker-icon').forEach(icon => {
      const rect = icon.getBoundingClientRect()
      const distance = Math.hypot(
        rect.left + rect.width / 2 - centerX,
        rect.top + rect.height / 2 - centerY,
      )
      const animations = getPinAnimations(distance / halfDiagonal)
      // Leaflet places the icon with a translate, and the scale is applied around the transform origin
      // after it, so the origin is the icon's centre once translated: the pop then scales the icon
      // where it is instead of moving it
      const position = L.DomUtil.getPosition(icon)
      if (position) {
        const origin = `calc(${position.x}px + 50%) calc(${position.y}px + 50%)`
        icon.style.setProperty('--pin-origin', origin)
      }
      icon.style.setProperty('--pin-animation', animations.pin)
      icon.style.setProperty('--pin-label-animation', animations.label)
    })
    container.classList.add(OPENING_CLASS)

    const stop = () => {
      clearTimeout(timer)
      map.off('movestart zoomstart', stop)
      container.classList.remove(OPENING_CLASS)
    }
    const timer = setTimeout(stop, getPinCascadeDuration() * 1000)
    map.on('movestart zoomstart', stop)

    return stop
  }, [isRevealed, map])

  return null
}

export default PinCascade
